import { useCallback } from "react";

interface SkipLinkProps {
  /** Target element ID (without #) */
  targetId?: string;
  /** Link text */
  label?: string;
}

/**
 * Skip link component for keyboard users to bypass navigation
 * Becomes visible on focus and smoothly scrolls to target
 */
export function SkipLink({ 
  targetId = "main-content", 
  label = "Skip to main content" 
}: SkipLinkProps) {
  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      // Scroll to element
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      // Set focus for screen readers
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
      // Clean up tabindex after blur
      target.addEventListener("blur", () => {
        target.removeAttribute("tabindex");
      }, { once: true });
    }
  }, [targetId]);

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:shadow-lg focus:animate-in focus:fade-in-0 focus:zoom-in-95"
    >
      {label}
    </a>
  );
}

/**
 * Multiple skip links for complex layouts
 */
export function SkipLinks({ 
  links 
}: { 
  links: Array<{ targetId: string; label: string }> 
}) {
  return (
    <nav aria-label="Skip links" className="contents">
      {links.map((link, index) => (
        <SkipLink key={index} {...link} />
      ))}
    </nav>
  );
}
